import { useState, useMemo } from 'react';
import './CalendarWidget.css';

const DAYS = ['일', '월', '화', '수', '목', '금', '토'];

function getMonthDays(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const rows = [];
  let week = new Array(firstDay).fill(null);

  for (let d = 1; d <= daysInMonth; d++) {
    week.push(d);
    if (week.length === 7) {
      rows.push(week);
      week = [];
    }
  }
  if (week.length > 0) {
    while (week.length < 7) week.push(null);
    rows.push(week);
  }
  return rows;
}

function toDateStr(y, m, d) {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function calcStreak(recordDates) {
  if (recordDates.size === 0) return 0;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const fmt = (dt) =>
    `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;

  let streak = 0;
  let cursor = new Date(today);

  // 오늘 기록이 없으면 어제부터 시작
  if (!recordDates.has(fmt(cursor))) {
    cursor.setDate(cursor.getDate() - 1);
  }

  while (recordDates.has(fmt(cursor))) {
    streak++;
    cursor.setDate(cursor.getDate() - 1);
  }

  return streak;
}

export default function CalendarWidget({ records, onSelectDate, onClose }) {
  const today = useMemo(() => new Date(), []);
  const todayStr = toDateStr(today.getFullYear(), today.getMonth(), today.getDate());

  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showYearPicker, setShowYearPicker] = useState(false);

  // 기록이 있는 날짜 Set
  const recordDateSet = useMemo(() => {
    const set = new Set();
    for (const r of records) {
      if (r.start_date) set.add(r.start_date);
    }
    return set;
  }, [records]);

  // 현재 보이는 월의 날짜별 기록 수
  const monthRecordMap = useMemo(() => {
    const map = new Map();
    const prefix = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}`;
    for (const r of records) {
      if (r.start_date?.startsWith(prefix)) {
        map.set(r.start_date, (map.get(r.start_date) || 0) + 1);
      }
    }
    return map;
  }, [records, viewYear, viewMonth]);

  const streak = useMemo(() => calcStreak(recordDateSet), [recordDateSet]);

  const weeks = getMonthDays(viewYear, viewMonth);

  const goPrev = () => {
    if (viewMonth === 0) {
      setViewYear(viewYear - 1);
      setViewMonth(11);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };

  const goNext = () => {
    if (viewMonth === 11) {
      setViewYear(viewYear + 1);
      setViewMonth(0);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };

  const goToday = () => {
    setViewYear(today.getFullYear());
    setViewMonth(today.getMonth());
    setSelectedDate(null);
  };

  const isCurrentMonth = viewYear === today.getFullYear() && viewMonth === today.getMonth();

  // 연도 목록 생성 (기록 속 최소 연도 ~ 현재+1년)
  const yearList = useMemo(() => {
    let minYear = today.getFullYear();
    for (const r of records) {
      if (r.start_date) {
        const y = parseInt(r.start_date.slice(0, 4), 10);
        if (y < minYear) minYear = y;
      }
    }
    const maxYear = today.getFullYear() + 1;
    const years = [];
    for (let y = maxYear; y >= minYear; y--) years.push(y);
    return years;
  }, [records, today]);

  const handleDayClick = (day) => {
    if (!day) return;
    const dateStr = toDateStr(viewYear, viewMonth, day);
    if (selectedDate === dateStr) {
      setSelectedDate(null);
      onSelectDate(null);
    } else {
      setSelectedDate(dateStr);
      onSelectDate(dateStr);
    }
  };

  return (
    <div className="calendar-widget">
      <div className="calendar-header">
        <div className="calendar-streak">
          🔥 {streak}일 연속 기록!
        </div>
        <button className="calendar-close-btn" onClick={onClose} aria-label="닫기">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <div className="calendar-nav">
        <button className="calendar-nav-btn" onClick={goPrev} aria-label="이전 달">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <button className="calendar-month-label" onClick={() => setShowYearPicker(!showYearPicker)}>
          {viewYear}년 {viewMonth + 1}월
          <svg className="calendar-month-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points={showYearPicker ? "18 15 12 9 6 15" : "6 9 12 15 18 9"} />
          </svg>
        </button>
        <button className="calendar-nav-btn" onClick={goNext} aria-label="다음 달">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 6 15 12 9 18" />
          </svg>
        </button>
        {!isCurrentMonth && (
          <button className="calendar-today-btn" onClick={goToday}>오늘</button>
        )}
      </div>

      {showYearPicker && (
        <div className="calendar-year-picker">
          {yearList.map((y) => (
            <button
              key={y}
              className={`calendar-year-item${y === viewYear ? ' calendar-year-item--active' : ''}`}
              onClick={() => { setViewYear(y); setShowYearPicker(false); }}
            >
              {y}년
            </button>
          ))}
        </div>
      )}

      <div className="calendar-grid">
        <div className="calendar-weekdays">
          {DAYS.map((d, i) => (
            <span key={d} className={`calendar-weekday${i === 0 ? ' calendar-weekday--sun' : i === 6 ? ' calendar-weekday--sat' : ''}`}>{d}</span>
          ))}
        </div>
        {weeks.map((week, wi) => (
          <div key={wi} className="calendar-week">
            {week.map((day, di) => {
              if (!day) return <span key={di} className="calendar-day calendar-day--empty" />;
              const dateStr = toDateStr(viewYear, viewMonth, day);
              const hasRecord = monthRecordMap.has(dateStr);
              const isSelected = selectedDate === dateStr;
              const isTodayCell = dateStr === todayStr;
              const isSun = di === 0;
              const isSat = di === 6;

              return (
                <button
                  key={di}
                  className={
                    'calendar-day' +
                    (isTodayCell ? ' calendar-day--today' : '') +
                    (isSelected ? ' calendar-day--selected' : '') +
                    (isSun ? ' calendar-day--sun' : '') +
                    (isSat ? ' calendar-day--sat' : '')
                  }
                  onClick={() => handleDayClick(day)}
                >
                  <span className="calendar-day-num">{day}</span>
                  {hasRecord && <span className="calendar-day-dot" />}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
